import axios from 'axios';
import { flow, getEnv, getRoot, onSnapshot, types } from 'mobx-state-tree';

import CheckoutPageModule, {
  CheckoutPageModuleType,
} from '../../../types/checkout/CheckoutPageModule';
import FormFieldId from '../../../types/FormFieldId';
import FormFieldType from '../../../types/FormFieldType';
import { OrderCheckoutTypeType } from '../../../types/order/OrderCheckoutType';
import RequestState, { RequestStateType } from '../../../types/RequestState';
import { invalidFormFields } from '../../../util/constants';
import { stringify } from '../../../util/queryString';
import CartBasket from '../../cart/CartBasket';
import CustomFormField from '../../CustomFormField';
import PickupPoint from '../../shipping/PickupPoint';
import Order from '../Order';
import OrderAlerts from '../OrderAlerts';
import OrderShippingOption from '../OrderShippingOption';
import OrderCheckoutProduct from './OrderCheckoutProduct';
import PaymentMethodGroup from './payment/PaymentMethodGroup';
import PaymentProvider from './payment/PaymentProvider';
import SelectedPayment from './payment/SelectedPayment';
import SelectedShipping from './shipping/SelectedShipping';

const OrderCheckoutModel = types
  .compose(
    Order,
    OrderAlerts,
    types.model('OrderCheckout', {
      after_confirm_redirect_url: types.maybeNull(types.string),
      baskets_and_templates: types.maybeNull(types.array(CartBasket)),
      checkout_type: OrderCheckoutTypeType,
      customer_fields: types.maybeNull(types.map(CustomFormField)),
      extra_fields: types.maybeNull(types.map(CustomFormField)),
      free_of_charge_product_info: types.optional(
        types.model('FreeOfChargeProductInfo', {
          max_products: types.number,
          min_order: types.model({
            with_tax: types.number,
            without_tax: types.number,
          }),
        }),
        {
          max_products: 0,
          min_order: {
            with_tax: 0,
            without_tax: 0,
          },
        }
      ),
      gift_voucher_recipient_email: types.maybeNull(types.string),
      has_gift_vouchers: types.boolean,
      id_for_analytics: types.string,
      info_pages: types.optional(
        types.model({
          DELIVERY_TERMS: types.maybeNull(types.integer),
          PRIVACY_TERMS: types.maybeNull(types.integer),
        }),
        {}
      ),
      number_of_products: types.number,
      payment_method_groups: types.maybeNull(types.array(PaymentMethodGroup)),
      payment_provider_module_id: types.integer,
      payment_providers: types.maybeNull(types.array(PaymentProvider)),
      pickupPoints: types.optional(types.map(types.array(PickupPoint)), {}),
      pickupPointState: types.optional(
        types.map(types.optional(RequestStateType, RequestState.NONE)),
        {}
      ),
      products: types.maybeNull(types.array(OrderCheckoutProduct)),
      real_total: types.number,
      removed_products: types.maybeNull(types.array(OrderCheckoutProduct)),
      scroll_to_alert: types.maybeNull(CheckoutPageModuleType),
      scroll_to_method: types.maybeNull(types.string),
      selected_basket_id: types.string,
      selected_payment: types.optional(SelectedPayment, {
        group: null,
        method: null,
        provider: null,
      }),
      selected_shipping: types.maybeNull(SelectedShipping),
      shipping_options: types.maybeNull(types.array(OrderShippingOption)),
      show_free_of_charge_products: types.boolean,
      show_recommended_products: types.boolean,
      show_registration: types.boolean,
      submit_button_type: types.string,
      total_weight: types.maybeNull(types.string),
    })
  )
  .named('OrderCheckout')
  .extend((self) => {
    // let sourcePickupPoints = null;

    const nonTextFieldTypes = new Set([
      FormFieldType.BOOLEAN,
      FormFieldType.SELECT,
      FormFieldType.BUTTON,
    ]);
    const countryCode = getRoot(self).countryStore.activeCountry?.iso_code_2;
    const getSeparateShippingAddress = () => {
      const deliveryPostalCode = self.customer_fields.get(
        FormFieldId.DELIVERY_POSTAL_CODE
      )?.value;
      if (deliveryPostalCode) {
        return {
          postCode: self.customer_fields.get(FormFieldId.DELIVERY_POSTAL_CODE)
            ?.value,
          city: self.customer_fields.get(FormFieldId.DELIVERY_CITY)?.value,
          streetAddress: self.customer_fields.get(
            FormFieldId.DELIVERY_STREET_ADDRESS
          )?.value,
        };
      }

      return {};
    };

    const getPrimaryShippingAddress = () => {
      const primaryPostalCode = self.customer_fields.get(
        FormFieldId.BILLING_POSTAL_CODE
      )?.value;
      if (primaryPostalCode) {
        return {
          postCode: self.customer_fields.get(FormFieldId.BILLING_POSTAL_CODE)
            ?.value,
          city: self.customer_fields.get(FormFieldId.BILLING_CITY)?.value,
          streetAddress: self.customer_fields.get(
            FormFieldId.BILLING_STREET_ADDRESS
          )?.value,
        };
      }

      return {};
    };

    const getShippingAddress = () => {
      const isSeparateCustomerAddress = self.customer_fields.get(
        FormFieldId.SEPARATE_DELIVERY_ADDRESS
      )?.value;
      let address = {};

      if (isSeparateCustomerAddress) {
        address = getSeparateShippingAddress();
      } else {
        address = getPrimaryShippingAddress();
      }

      return Object.keys(address).length > 0 ? address : null;
    };
    const getSelectedShippingModule = () =>
      self.selected_shipping ? self.selected_shipping.module : null;
    const getSelectedPaymentModule = () => self.payment_provider_module_id;

    return {
      views: {
        get billingFields() {
          return self.customerFieldsList.filter(
            (field) =>
              field.id.startsWith('BILLING_') &&
              !invalidFormFields.checkout.has(field.type)
          );
        },
        get cartAlerts() {
          return self.getModuleAlerts(CheckoutPageModule.CART_MODULE);
        },
        get cartIsEmpty() {
          return (
            self.products.length === 0 ||
            self.products.every((product) => product.quantity === 0)
          );
        },
        get customerAddress() {
          return getShippingAddress();
        },
        get customerFieldsList() {
          return [...self.customer_fields.values()];
        },
        get customerInfoAlerts() {
          return self.getModuleAlerts(CheckoutPageModule.CUSTOMER_INFO_MODULE);
        },
        get currentPickupPointList() {
          return self.pickupPoints.get(self.pickupPointIdentifier) || [];
        },
        get currentPickupPointState() {
          return self.pickupPointState.get(self.pickupPointIdentifier);
        },
        get deliveryFields() {
          return self.customerFieldsList.filter(
            (field) =>
              field.id.startsWith('DELIVERY_') &&
              !nonTextFieldTypes.has(field.type)
          );
        },
        get extraFields() {
          return [...self.extra_fields.values()];
        },
        get hasCustomerFields() {
          return self.customer_fields?.size > 0 || false;
        },
        get hasTerms() {
          return (
            self.info_pages.DELIVERY_TERMS !== null ||
            self.info_pages.PRIVACY_TERMS !== null
          );
        },
        get hasPaymentProviders() {
          return self.payment_providers?.length > 0 || false;
        },
        get paymentMethodGroups() {
          const originalGroups = self.payment_method_groups;
          const groups = originalGroups.filter(
            (group) => group.type !== 'CUSTOM'
          );
          const customGroups = originalGroups
            .filter((group) => group.type === 'CUSTOM')
            .reduce((acc, group) => acc.concat(group.methods), []);

          return {
            groups,
            customGroups,
          };
        },
        get paymentMethodAlerts() {
          return self.getModuleAlerts(CheckoutPageModule.PAYMENT_METHOD_MODULE);
        },
        get pickupPointIdentifier() {
          const selectedShippingModule = getSelectedShippingModule();
          const selectedPaymentModule = getSelectedPaymentModule();
          return stringify({
            shippingModuleId: selectedShippingModule?.id,
            paymentModuleId: selectedPaymentModule,
            ...self.customerAddress,
          });
        },
        get pickupPointIsLoading() {
          return (
            self.pickupPointState.get(self.pickupPointIdentifier) ===
            RequestState.LOADING
          );
        },
        get productList() {
          return self.products;
        },
        get recommendedProductsIds() {
          return self.products.map((product) => product.product_id);
        },
        get selectedPayment() {
          return {
            provider: self.selected_payment?.provider?.id || null,
            group: self.selected_payment?.group?.type || null,
            method: self.selected_payment?.method?.id || null,
          };
        },
        get selectedPickupPoint() {
          return (
            self.currentPickupPointList.find(
              (pp) => pp.unique_id === self.selected_shipping.pickup_point
            ) || self.currentPickupPointList[0]
          );
        },
        get selectedShipping() {
          if (self.selected_shipping) {
            const selectedShipping = self.selected_shipping;

            return {
              module: selectedShipping.module?.id || null,
              pickup_point: self.selectedPickupPoint,
            };
          }

          return null;
        },
        get shippingInfoAlerts() {
          return self.getModuleAlerts(CheckoutPageModule.SHIPPING_INFO_MODULE);
        },
        get shippingMethodAlerts() {
          return self.getModuleAlerts(
            CheckoutPageModule.SHIPPING_METHOD_MODULE
          );
        },
        get summaryAlerts() {
          return self.getModuleAlerts(CheckoutPageModule.SUMMARY_MODULE);
        },
        get validShipping() {
          return !!self.pickupPoints.get(self.pickupPointIdentifier);
        },
        getCustomerField(fieldId) {
          const field = self.customer_fields.get(fieldId);
          return {
            field,
            value: field && field.value,
          };
        },
      },
      actions: {
        afterAttach() {
          if (self.cartIsEmpty) {
            return;
          }

          onSnapshot(self.selected_shipping, () => {
            if (Array.isArray(self.shipping_options)
              && self?.shipping_options.length > 0
              && self.selected_shipping
              && self.selected_shipping.module
              && self.selected_shipping.module.requires_pickup_point) {
              self.fetchPickupPoints();
            }
          });
        },
        fetchPickupPoints: flow(function* fetchPickupPoints() {
          const customerAddress = self.customerAddress;
          const selectedShippingModule = getSelectedShippingModule();
          const selectedPaymentModule = getSelectedPaymentModule();
          const hasFetched =
            self.pickupPointState.get(self.pickupPointIdentifier) ===
              RequestState.LOADED || self.pickupPointIsLoading;

          if (
            hasFetched ||
            !customerAddress?.postCode ||
            !selectedShippingModule ||
            !countryCode ||
            !selectedPaymentModule
          ) {
            return;
          }

          self.pickupPointState.set(
            self.pickupPointIdentifier,
            RequestState.LOADING
          );

          const payload = {
            shippingModuleId: selectedShippingModule.id,
            paymentModuleId: selectedPaymentModule,
            countryCode,
            ...customerAddress,
          };

          try {
            const pickupPoints = yield getEnv(self).apiWrapper.request(
              `shipping/pickup-points`,
              {
                params: payload,
              }
            );

            self.pickupPoints.set(self.pickupPointIdentifier, pickupPoints);
            self.pickupPointState.set(
              self.pickupPointIdentifier,
              RequestState.LOADED
            );
          } catch (error) {
            if (axios.isCancel(error)) {
              self.pickupPointState.set(
                self.pickupPointIdentifier,
                RequestState.NONE
              );
            } else {
              self.pickupPointState.set(
                self.pickupPointIdentifier,
                RequestState.ERROR
              );

              throw error;
            }
          }
        }),
        updateSelectedPayment({
          paymentMethodGroupId,
          paymentProviderId,
          paymentMethodId,
        }) {
          if (paymentMethodGroupId) {
            self.selected_payment.updatePaymentMethodGroup({
              id: paymentMethodGroupId,
              groups: self.payment_method_groups,
            });
          }

          if (paymentMethodId) {
            self.selected_payment.updatePaymentMethod({
              id: paymentMethodId,
              methods: self.payment_method_groups,
            });
          }

          if (paymentProviderId) {
            self.selected_payment.updatePaymentProvider({
              id: paymentProviderId,
              providers: self.payment_providers,
            });
          }
        },
        updateSelectedShipping({ shippingModuleId, pickupPointId }) {
          if (shippingModuleId) {
            self.selected_shipping.updateSelectedModule({
              id: shippingModuleId,
              options: self.shipping_options,
            });
          }

          if (pickupPointId) {
            self.selected_shipping.updateSelectedPickupPoint({
              id: pickupPointId,
            });
          }
        },
        setScrollToMethodId(id) {
          self.scroll_to_method = id;
        },
        resetPickupPointState(pickupPointIdentifier) {
          self.pickupPointState.set(
            pickupPointIdentifier,
            RequestState.NONE
          );
        }
      },
    };
  });

const OrderCheckout = types.snapshotProcessor(OrderCheckoutModel, {
  preProcessor(snapshot) {
    const convertItemsToMap = (items) => {
      return items.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
    };

    return {
      ...snapshot,
      customer_fields: convertItemsToMap(snapshot.customer_fields),
      extra_fields: convertItemsToMap(snapshot.extra_fields),
    };
  },
});

export default OrderCheckout;
